/**
 * eventmanager feature flag catalog — the single inventory of flags this app reads,
 * grouped by domain. Access as FEATURE_FLAG.<DOMAIN>.<FLAG>, e.g.
 * FEATURE_FLAG.FESTIVAL.IS_NETWORKING_ENABLED.
 *
 * Values are the literal PostHog flag keys.
 *
 * ── Legacy keys & migration ───────────────────────────────────────────────────
 * The three entries below still point at the original `is_*_enabled` PostHog
 * keys (they predate the omr-js descriptor convention). New flags MUST use the
 * `flag()` descriptor form so the key auto-resolves to
 * `manager__<domain>__<feature>__<type>` (see the omrjs-core-ui posthog README
 * and `docs/feature-flags.md`).
 *
 * To migrate a legacy entry to the new format:
 *
 *   1. Pick the target type suffix — `release` (still rolling out), `ops`
 *      (permanent kill-switch), `perm` (entitlement), `exp` (experiment).
 *   2. Create the new flag in PostHog under the `manager/` folder (or `shared/`
 *      for cross-app flags) with the resolved key, e.g.
 *      `manager__festival__networking__release`. Tag it `app:<app>`,
 *      `type:<type>`, `domain:<domain>`.
 *   3. Mirror the old flag's rollout %, property filters and cohort targeting
 *      onto the new flag.
 *   4. Dual-evaluate for at least one release cycle (read both old and new
 *      keys, log when they disagree) until you're confident parity holds.
 *   5. Swap the catalog entry from the string to `flag({ ... })` in a single
 *      PR — consumer code (`FEATURE_FLAG.FESTIVAL.<NAME>`) is unchanged.
 *   6. Once the new key has been live in prod for a release, delete the old
 *      PostHog flag.
 *
 * For SHARED flags read by more than one OMR app (e.g. `IS_MEETINGS_ENABLED`
 * is also read by `festival-companion`), update every consuming repo to point
 * at the new key before step 5 — or dual-read during the transition.
 */
import { flag } from '@ramp106/omrjs-core-ui/posthog'

export const FEATURE_FLAG = {
  FESTIVAL: {
    /**
     * @deprecated Legacy key. Migrate to
     *   `flag({ domain: 'festival', feature: 'networking', type: 'release' })`
     *   which resolves to `manager__festival__networking__release`.
     *   See the migration recipe in the file header.
     */
    IS_NETWORKING_ENABLED: 'is_networking_enabled',

    /**
     * @deprecated Legacy SHARED key, also read by `festival-companion`.
     *   Migrate to `flag({ app: 'shared', domain: 'festival', feature: 'meetings', type: 'ops' })`
     *   which resolves to `shared__festival__meetings__ops`.
     *   Coordinate cross-repo before swapping. See the migration recipe in the file header.
     */
    IS_MEETINGS_ENABLED: 'is_meetings_enabled',

    /**
     * @deprecated Legacy key. Migrate to
     *   `flag({ domain: 'festival', feature: 'lead_gen', type: 'release' })`
     *   which resolves to `manager__festival__lead_gen__release`.
     *   See the migration recipe in the file header.
     */
    IS_LEAD_GEN_ENABLED: 'is_lead_gen_enabled',
  },
  REVIEWS_PRODUCTS: {
    RANKSCALE_BETA_SIGNUP_RELEASE: flag({
      domain: 'reviews_products',
      feature: 'rankscale_beta_signup',
      type: 'release',
    }),
    LEADS_RELEASE: flag({
      domain: 'reviews_products',
      feature: 'leads',
      type: 'release',
    }),
    TOP_PRODUCTS_RANKING_RELEASE: flag({
      domain: 'reviews_products',
      feature: 'top_100_ranking',
      type: 'release',
    }),
    INSIGHTS_HUB_RELEASE: flag({
      domain: 'reviews_products',
      feature: 'insights_hub',
      type: 'release',
    }),
  },
  SHARED: {
    NEW_INCENTIVIZATION_TYPES_RELEASE: flag({
      app: 'shared',
      domain: 'incentives',
      feature: 'new_campaign_types',
      type: 'release',
    }),
  },
  REVIEWS_AGENCIES: {
    REVIEWS_RELEASE: flag({
      domain: 'reviews_agencies',
      feature: 'reviews',
      type: 'release',
    }),
    LEADS_RELEASE: flag({
      domain: 'reviews_agencies',
      feature: 'leads',
      type: 'release',
    }),
  },
  REVIEWS_INTEGRATIONS: {
    BUYER_INTENT_MCP_RELEASE: flag({
      domain: 'reviews_integrations',
      feature: 'buyer_intent_mcp',
      type: 'release',
    }),
  },
  NOTIFICATIONS: {
    FEED_RELEASE: flag({
      domain: 'notifications',
      feature: 'feed',
      type: 'release',
    }),
    CENTER_RELEASE: flag({
      domain: 'notifications',
      feature: 'center',
      type: 'release',
    }),
  },
} as const
